import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle } from 'lucide-react';
import { NAV_LINKS, whatsappLink } from '@/lib/constants';
import BrandMark from './BrandMark';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border] duration-300 ${
        scrolled ? 'bg-[#07111F]/80 backdrop-blur-xl border-b border-white/[0.06]' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 h-20 md:h-24 flex items-center justify-between">
        <BrandMark />

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={`nav-${l.to.replace(/\//g, '') || 'home'}`}
              className={({ isActive }) =>
                `text-[13px] font-inter font-medium tracking-wide uppercase transition-colors ${
                  isActive ? 'text-[#FFD400]' : 'text-white/70 hover:text-white'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="nav-whatsapp-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-inter font-medium text-white/90 hover:border-[#39A8FF] hover:text-white transition-colors"
          >
            <MessageCircle className="h-4 w-4 text-[#39A8FF]" strokeWidth={2} />
            WhatsApp
          </a>
          <Link
            to="/calculator"
            data-testid="nav-calculate-btn"
            className="inline-flex items-center rounded-full bg-[#FFD400] px-5 py-2.5 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F] hover:shadow-[0_0_32px_rgba(255,212,0,0.35)] transition-shadow"
          >
            Calculate Claim
          </Link>
        </div>

        <button
          data-testid="nav-menu-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#121212]/60"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#07111F]/95 backdrop-blur-xl border-t border-white/5" data-testid="mobile-menu">
          <nav className="px-5 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                data-testid={`mobile-nav-${l.to.replace(/\//g, '') || 'home'}`}
                className={({ isActive }) =>
                  `font-poppins text-2xl font-semibold tracking-tight ${isActive ? 'text-[#FFD400]' : 'text-white'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-4 flex flex-col gap-3">
              <Link
                to="/calculator"
                data-testid="mobile-nav-calculate-btn"
                className="rounded-full bg-[#FFD400] px-6 py-4 text-center text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F]"
              >
                Calculate Your Claim
              </Link>
              <a
                href={whatsappLink()}
                data-testid="mobile-nav-whatsapp-btn"
                target="_blank"
                rel="noreferrer noopener"
                className="rounded-full border border-white/20 px-6 py-4 text-center text-sm font-inter font-semibold text-white"
              >
                Chat on WhatsApp
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
